import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { Banner, PageHeaderWrapper, ProgressBar } from "@src/appV2/redesign/system/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { PageWrapper } from "../../components/PageWrapper";
import { invalidateShifts } from "../../invalidateShifts";
import { useNCOrientationStatus } from "../../ShiftDiscovery/NorthCarolinaOrientation/useNCOrientationStatus";
import { SHIFT_DISCOVERY_LIST_PATH } from "../../ShiftDiscovery/paths";
import { useHasActionRequiredForMandatoryRequirements } from "../api/useHasActionRequiredForMandatoryRequirements";
import { DocumentPageScrollWrapper } from "../Details/ScrollWrapper";
import { DOCUMENTS_BASE_PATH_FULL } from "../paths";
import { NCOrientationRequirementsList } from "./RequirementsList";

export function NCOrientationPage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { orientationRequirements, allCompleted, isLoading, completedCount, totalCount } =
    useNCOrientationStatus();
  const hasActionRequired = useHasActionRequiredForMandatoryRequirements();

  return (
    <PageWrapper>
      <PageHeaderWrapper variant="tertiary">
        <Stack spacing={1}>
          <PageHeaderTopRow title="North Carolina Orientation" />
          <Box sx={{ paddingX: 7, paddingBottom: 7 }}>
            <ProgressBar
              showCount
              completed={completedCount}
              total={totalCount}
              countSuffix="approved"
            />
          </Box>
        </Stack>
      </PageHeaderWrapper>

      <DocumentPageScrollWrapper ref={scrollRef} sx={{ paddingTop: 5 }}>
        {isLoading && (
          <Stack spacing={5} sx={{ paddingX: 5 }}>
            <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={80} sx={{ borderRadius: 3 }} />
            <Skeleton variant="rounded" height={80} sx={{ borderRadius: 3 }} />
          </Stack>
        )}

        {!isLoading && !allCompleted && (
          <>
            <Banner
              illustrationType="unlock-small"
              title="North Carolina Requirements"
              description="Professionals must complete all orientation requirements before shifts can be booked in North Carolina. These are North Carolina state government requirements."
            />

            {isDefined(orientationRequirements) && (
              <NCOrientationRequirementsList orientationRequirements={orientationRequirements} />
            )}
          </>
        )}

        {!isLoading && allCompleted === true && hasActionRequired && (
          <Banner
            intent="success"
            illustrationType="success"
            title="Orientation complete!"
            description="Great work completing your North Carolina orientation. Complete your remaining documents to start booking shifts."
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                history.push(DOCUMENTS_BASE_PATH_FULL);
              }}
            >
              Complete your documents
            </Button>
          </Banner>
        )}

        {!isLoading && allCompleted === true && !hasActionRequired && (
          <Banner
            intent="success"
            illustrationType="success"
            title="Orientation complete!"
            description="Great work completing your North Carolina orientation. You are ready to book shifts."
          >
            <Button
              fullWidth
              variant="contained"
              onClick={async () => {
                await invalidateShifts(queryClient);
                history.push(`${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`);
              }}
            >
              Find Shifts
            </Button>
          </Banner>
        )}
      </DocumentPageScrollWrapper>
    </PageWrapper>
  );
}
